import clsx from 'clsx'
import Image from 'next/legacy/image'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import { LinkType } from '../../generated/content'
import type { ImageResult } from '../data/fragments/ImageFragment'
import { Button } from './Buttons'
import { Icon } from './Icon'
import { Likes } from './Likes'
import styles from './ProfileBadge.module.sass'
import { UserBadge } from './UserBadge'

export type ProfileBadgeProps = {
	type: 'public' | 'profile'
	image: ImageResult
	name: string
	statistics?: ProfileBadgeStatistics
	userId: string
}

type ProfileBadgeStatistics = {
	duplications?: number
	likes?: number
	plants?: number
}

export const ProfileBadge: FunctionComponent<ProfileBadgeProps> = ({
	type,
	image,
	name,
	statistics,
	userId,
}) => {
	const locale = useRouter().locale
	return (
		<div className={clsx(styles.wrapper, styles[`is_${type}`])}>
			<div className={styles.main}>
				{type === 'public' ? (
					<>
						<UserBadge
							name={name}
							image={image}
							size="big"
							isHighlighted
							link={{
								type: LinkType.external,
								externalLink: `/author/${userId}`,
							}}
						/>
						<ProfileBadgeStatistics {...statistics} />
					</>
				) : type === 'profile' ? (
					<>
						<div className={styles.image}>
							<Image
								src={image.url}
								layout="fill"
								objectFit="cover"
								alt={image.alt ?? image.description ?? ''}
								placeholder="blur"
								blurDataURL={image.url}
							/>
						</div>
						<div className={styles.content}>
							<h3 className={styles.name}>{name}</h3>
							<div className={styles.contentIn}>
								<ProfileBadgeStatistics {...statistics} />
								<Button href={`/user/edit`} type="link" fill="empty" isBorder>
									{locale === 'cs' ? 'Upravit profil' : 'Edit profile'}
								</Button>
							</div>
						</div>
					</>
				) : null}
			</div>
		</div>
	)
}

const ProfileBadgeStatistics: FunctionComponent<ProfileBadgeStatistics> = ({
	likes = 0,
	plants = 0,
}) => {
	return (
		<div className={styles.statistics}>
			<div className={clsx(styles.statisticsItem, styles.is_likes)}>
				<Likes count={likes} isNotButton />
				{/* @TODO: translations */}
				<div className={styles.tooltip}>Likes</div>
			</div>
			<div className={clsx(styles.statisticsItem, styles.is_plants)}>
				<div className={styles.icon}>
					<Icon name="plant" />
				</div>
				<span>{plants}</span>
				{/* @TODO: translations */}
				<div className={styles.tooltip}>Plant profiles</div>
			</div>
		</div>
	)
}
